'use client';

import { Popover } from '@base-ui/react/popover';
import { useComputed, useObservable } from '@legendapp/state/react';
import { type ReactNode, createContext, useContext } from 'react';
import type { Observable } from '@legendapp/state';
import { type SearchFormValues } from '@/components/forms/SearchFromProvider';
import { useBreakpointDown$ } from '@/hooks/useBreakpointDown$';

export type HeroBlockSearchFormTriggerId = 'fleet-type' | 'locations' | 'dates';
export type HeroBlockSearchFormWarnings = Record<keyof SearchFormValues, ReactNode>;

export type HeroBlockSearchFormState = {
    popover: ReturnType<typeof Popover.createHandle<ReactNode>>;
    triggerId$: Observable<HeroBlockSearchFormTriggerId | null>;
    mode$: Observable<'default' | 'modal'>;
    variant$: Observable<'horizontal' | 'vertical'>;
    isSm$: Observable<boolean>;
    open$: Observable<boolean>;
    showStickySearchBtn$: Observable<boolean>;
    values: Observable<Partial<SearchFormValues>>;
    warnings$: Observable<Partial<HeroBlockSearchFormWarnings>>;
};

const HeroBlockSearchFormContext = createContext<HeroBlockSearchFormState>(undefined as unknown as HeroBlockSearchFormState);

export type HeroBlockSearchFormProviderProps = {
    children: ReactNode;
    values?: Partial<SearchFormValues>;
    mode?: 'default' | 'modal';
};
export function HeroBlockSearchFormProvider({ children, values, mode }: HeroBlockSearchFormProviderProps) {
    const mode$ = useObservable(mode || 'default');
    const isSm$ = useBreakpointDown$('lg');
    const variant$ = useComputed(() => {
        const isModal = mode$.get() === 'modal';
        const isSm = isSm$.get();
        return isModal || isSm ? 'vertical' : 'horizontal';
    });

    const state = {
        popover: Popover.createHandle<ReactNode>(),
        mode$,
        isSm$,
        variant$,
        triggerId$: useObservable(null),
        open$: useObservable(false),
        showStickySearchBtn$: useObservable(false),
        values: useObservable<Partial<SearchFormValues>>(values) as HeroBlockSearchFormState['values'],
        warnings$: useObservable<Partial<HeroBlockSearchFormWarnings>>({}),
    };

    return <HeroBlockSearchFormContext value={state}>{children}</HeroBlockSearchFormContext>;
}

export function useHeroBlockSearchFormState() {
    const context = useContext(HeroBlockSearchFormContext);
    if (!context) {
        throw new Error('useHeroBlockSearchFormState must be used within a HeroBlockSearchFormProvider');
    }
    return context;
}
